import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { RouteMetadataManager } from './components/seo/route-metadata-manager';
import { AuthProvider } from './features/auth/auth-context';
import { CreateAccountPage } from './features/auth/create-account-page';
import { ForgotPasswordPage } from './features/auth/forgot-password-page';
import { LoginPage } from './features/auth/login-page';
import { LandingPage } from './features/marketing/landing-page';
import { ProtectedRoute } from './features/auth/protected-route';
import { ResetPasswordPage } from './features/auth/reset-password-page';
import { CookiePolicyPage } from './features/legal/cookie-policy-page';
import { DpaPage } from './features/legal/dpa-page';
import { PrivacyPolicyPage } from './features/legal/privacy-policy-page';
import { TermsPage } from './features/legal/terms-page';

function lazyPage(loader: () => Promise<Record<string, unknown>>, exportName: string) {
  return React.lazy(async () => {
    const module = await loader();

    return {
      default: module[exportName] as React.ComponentType<any>,
    };
  });
}

const AnalyticsPage = lazyPage(
  () => import('./features/analytics/analytics-page'),
  'AnalyticsPage'
);
const CalendarPage = lazyPage(
  () => import('./features/calendar/calendar-page'),
  'CalendarPage'
);
const DashboardPage = lazyPage(
  () => import('./features/dashboard/dashboard-page'),
  'DashboardPage'
);
const ClientsPage = lazyPage(
  () => import('./features/clients/clients-page'),
  'ClientsPage'
);
const ClientDetailPage = lazyPage(
  () => import('./features/clients/client-detail-page'),
  'ClientDetailPage'
);
const AvailabilitySettingsPage = lazyPage(
  () => import('./features/settings/availability-settings-page'),
  'AvailabilitySettingsPage'
);
const BusinessSettingsPage = lazyPage(
  () => import('./features/settings/business-settings-page'),
  'BusinessSettingsPage'
);
const PersonalSettingsPage = lazyPage(
  () => import('./features/settings/personal-settings-page'),
  'PersonalSettingsPage'
);
const ServicesSettingsPage = lazyPage(
  () => import('./features/services/services-settings-page'),
  'ServicesSettingsPage'
);
const SpacesSettingsPage = lazyPage(
  () => import('./features/spaces/spaces-settings-page'),
  'SpacesSettingsPage'
);
const StaffSettingsPage = lazyPage(
  () => import('./features/staff/staff-settings-page'),
  'StaffSettingsPage'
);
const BusinessOnboardingPage = lazyPage(
  () => import('./features/onboarding/business-onboarding-page'),
  'BusinessOnboardingPage'
);
const PublicCancellationPage = lazyPage(
  () => import('./features/public-booking/public-cancellation-page'),
  'PublicCancellationPage'
);
const PublicBookingPage = lazyPage(
  () => import('./features/public-booking/public-booking-page'),
  'PublicBookingPage'
);
const PublicBookingPrivacyPage = lazyPage(
  () => import('./features/public-booking/public-booking-privacy-page'),
  'PublicBookingPrivacyPage'
);

function RouteLoadingFallback() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-[#f7f2ea] px-6 text-[#16323b]">
      <div className="rounded-[1.8rem] border border-white/70 bg-white/85 px-6 py-5 text-sm font-medium shadow-[0_20px_40px_rgba(48,38,25,0.08)]">
        A carregar a página...
      </div>
    </main>
  );
}

function App() {
  return (
    <AuthProvider>
      <RouteMetadataManager />
      <React.Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/dpa" element={<DpaPage />} />
          <Route path="/privacidade" element={<PrivacyPolicyPage />} />
          <Route path="/cookies" element={<CookiePolicyPage />} />
          <Route path="/termos" element={<TermsPage />} />
          <Route path="/b/:slug/privacy" element={<PublicBookingPrivacyPage />} />
          <Route path="/b/:slug" element={<PublicBookingPage />} />
          <Route path="/cancel/:token" element={<PublicCancellationPage />} />
          <Route path="/cancel-appointment" element={<PublicCancellationPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<CreateAccountPage />} />
          <Route path="/recover-password" element={<ForgotPasswordPage />} />
          <Route path="/forgot-password" element={<ResetPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute businessState="has-business">
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calendar"
            element={
              <ProtectedRoute businessState="has-business">
                <CalendarPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute businessState="has-business">
                <AnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients"
            element={
              <ProtectedRoute businessState="has-business">
                <ClientsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/:clientId"
            element={
              <ProtectedRoute businessState="has-business">
                <ClientDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/personal"
            element={
              <ProtectedRoute>
                <PersonalSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/business"
            element={
              <ProtectedRoute businessState="has-business">
                <BusinessSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/staff"
            element={
              <ProtectedRoute businessState="has-business">
                <StaffSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/services"
            element={
              <ProtectedRoute businessState="has-business">
                <ServicesSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/spaces"
            element={
              <ProtectedRoute businessState="has-business">
                <SpacesSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/availability"
            element={
              <ProtectedRoute businessState="has-business">
                <AvailabilitySettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute businessState="missing-business">
                <BusinessOnboardingPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </React.Suspense>
    </AuthProvider>
  );
}

export default App;
